import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AD_IMG_2 } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = ["Online Featured", "Print Full Page", "Print + Online", "Cover Sponsor"];

const empty = {
  name: "",
  business: "",
  email: "",
  phone: "",
  city: "",
  category: "",
  package: "",
  message: "",
};

const field =
  "w-full bg-transparent border-b border-white/25 py-3 text-white placeholder-white/40 font-light focus:border-white outline-none transition-colors";

const AdvertiseSection = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => setCities(r.data.map((c) => c.name)));
    axios.get(`${API}/categories`).then((r) => setCategories(r.data));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business || !form.email || !form.city) {
      toast.error("Please fill in your name, business, email and city.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/advertise`, form);
      toast.success("Received. Our team will reach out within 48 hours.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="advertise"
      data-testid="advertise-section"
      className="relative bg-[#0a0a0a] text-white overflow-hidden grain"
    >
      <img
        src={AD_IMG_2}
        alt=""
        className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-15 hidden lg:block"
      />
      <div className="relative px-4 md:px-8 lg:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="font-accent uppercase text-xs tracking-[0.3em] text-white/50">
            Be Featured
          </span>
          <h2 className="font-serif-display text-5xl md:text-7xl tracking-tight leading-[0.9] mt-3">
            Advertise
            <br />
            <span className="italic font-light">with us</span>
          </h2>
          <p className="mt-6 max-w-md font-light text-white/70 leading-relaxed">
            Put your business in front of a style-driven Texas audience — in the
            magazine and across the directory. Tell us about you and we'll build
            the right placement.
          </p>
          <ul className="mt-10 space-y-4">
            {PACKAGES.map((p) => (
              <li
                key={p}
                className="flex items-center gap-4 border-b border-white/10 pb-4"
              >
                <span className="font-serif-display italic text-white/50 text-xl w-8">
                  ✦
                </span>
                <span className="font-accent uppercase text-sm tracking-[0.15em]">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          data-testid="advertise-form"
          className="space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <input data-testid="ad-name" className={field} placeholder="Your name *" value={form.name} onChange={set("name")} />
            <input data-testid="ad-business" className={field} placeholder="Business name *" value={form.business} onChange={set("business")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <input data-testid="ad-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
            <input data-testid="ad-phone" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <select data-testid="ad-city" className={`${field} [&>option]:text-black`} value={form.city} onChange={set("city")}>
              <option value="">Select city *</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select data-testid="ad-category" className={`${field} [&>option]:text-black`} value={form.category} onChange={set("category")}>
              <option value="">Category</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <select data-testid="ad-package" className={`${field} [&>option]:text-black`} value={form.package} onChange={set("package")}>
            <option value="">Preferred package</option>
            {PACKAGES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <textarea data-testid="ad-message" rows={3} className={field} placeholder="Tell us about your business" value={form.message} onChange={set("message")} />
          <button
            type="submit"
            disabled={loading}
            data-testid="ad-submit"
            className="w-full sm:w-auto font-accent uppercase text-xs tracking-[0.25em] bg-white text-black px-10 py-4 hover:bg-white/80 transition-colors disabled:opacity-50"
          >
            {loading ? "Sending…" : "Request Placement"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default AdvertiseSection;
